import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ContractCard } from '../../components/Contracts/ContractCard';
import { ContractWithUsers, ContractFilterTab, ContractViewMode } from '../../types/contract';
import { contractService } from '../../services/contractService';
import { useContracts } from '../../hooks/useContracts';
import { FileText, Plus, Loader, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/Toast';

const Contracts = () => {
  const { ToastWrapper } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read viewMode from URL query parameter, default to 'client'
  const urlViewMode = searchParams.get('view') as ContractViewMode | null;
  const initialViewMode: ContractViewMode =
    urlViewMode === 'creator' || urlViewMode === 'client' ? urlViewMode : 'client';

  const [activeTab, setActiveTab] = useState<ContractFilterTab>('all');
  const [viewMode, setViewMode] = useState<ContractViewMode>(initialViewMode);

  const { contracts, currentUserId, loading, refresh } = useContracts(viewMode);

  // Update URL when viewMode changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (viewMode !== params.get('view')) {
      params.set('view', viewMode);
      navigate(`/contracts?${params.toString()}`, { replace: true });
    }
  }, [viewMode, navigate, searchParams]);

  // Filter contracts based on tab (status)
  const filteredContracts = useMemo(() => {
    // Already filtered by role via API (effectively)

    // Safety check: Client should NEVER see drafts
    let relevantContracts = contracts;
    if (viewMode === 'client') {
      relevantContracts = contracts.filter((c: ContractWithUsers) => c.status !== 'DRAFT');
    }

    switch (activeTab) {
      case 'pending_approval':
        return relevantContracts.filter((c) => c.status === 'PENDING_CLIENT_APPROVAL');
      case 'active':
        return relevantContracts.filter((c) => c.status === 'ACTIVE');
      case 'completed':
        return relevantContracts.filter((c) => c.status === 'COMPLETED');
      case 'cancelled':
        return relevantContracts.filter((c) => c.status === 'CANCELLED');
      case 'draft':
        return relevantContracts.filter((c) => c.status === 'DRAFT');
      default:
        // 'all' includes drafts for creator
        return relevantContracts;
    }
  }, [contracts, activeTab, viewMode]);

  // Count contracts by status
  const counts = useMemo(() => {
    const relevantContracts = viewMode === 'client'
      ? contracts.filter((c: ContractWithUsers) => c.status !== 'DRAFT')
      : contracts;
    return {
      all: relevantContracts.length,
      pending_approval: relevantContracts.filter((c) => c.status === 'PENDING_CLIENT_APPROVAL').length,
      active: relevantContracts.filter((c) => c.status === 'ACTIVE').length,
      completed: relevantContracts.filter((c) => c.status === 'COMPLETED').length,
      cancelled: relevantContracts.filter((c) => c.status === 'CANCELLED').length,
      draft: relevantContracts.filter((c) => c.status === 'DRAFT').length
    };
  }, [contracts, viewMode]);

  const tabs: { key: ContractFilterTab; label: string }[] = useMemo(() => {
    const baseTabs: { key: ContractFilterTab; label: string }[] = [
      { key: 'all', label: '全部' },
      { key: 'pending_approval', label: '待审核' },
      { key: 'active', label: '执行中' },
      { key: 'completed', label: '已完成' },
      { key: 'cancelled', label: '已取消' },
    ];
    if (viewMode === 'creator') {
      baseTabs.splice(1, 0, { key: 'draft', label: '草稿' });
    }
    return baseTabs;
  }, [viewMode]);

  return (
    <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
      <ToastWrapper />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-4 group w-fit"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              <span className="text-sm font-medium">返回</span>
            </button>
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3 mb-2">
              <FileText className="w-10 h-10 text-blue-600" />
              合同管理
            </h1>
          </div>
          {viewMode === 'creator' && (
            <div className="flex flex-col items-end gap-4">
              <Button
                variant="primary"
                size="lg"
                className="w-full md:w-auto"
                onClick={() => {
                  navigate('/contracts/create');
                }}
              >
                <Plus className="w-5 h-5 mr-2" />
                创建合同
              </Button>
            </div>
          )}
        </div>

        {/* View Mode & Tabs */}
        <div className="mb-8 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="inline-flex bg-slate-100 rounded-xl p-1">
            <button
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'client' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setViewMode('client')}
            >
              我是需求方
            </button>
            <button
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'creator' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setViewMode('creator')}
            >
              我是创作者
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 bg-gray-50/50 p-1.5 rounded-[2rem] w-fit xl:ml-auto border border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'bg-white text-blue-600 shadow-md ring-1 ring-black/5'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
                {counts[tab.key] > 0 && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.key
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {counts[tab.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Contract Grid */}
        {filteredContracts.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredContracts.map((contract) => (
              <ContractCard
                key={contract.contract_id}
                contract={contract}
                viewMode={viewMode}
                currentUserId={currentUserId}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
              <FileText className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-xl font-medium text-slate-900 mb-2">暂无合同</h3>
            <p className="text-slate-600 mb-6">
              {activeTab === 'all'
                ? viewMode === 'client'
                  ? '你还没有作为客户的合同'
                  : '你还没有作为创作者的合同'
                : `暂无${tabs.find((t) => t.key === activeTab)?.label}的合同`}
            </p>
            {viewMode === 'creator' && activeTab === 'all' && (
              <Button
                variant="primary"
                onClick={() => {
                  navigate('/contracts/create');
                }}
              >
                <Plus className="w-5 h-5 mr-2" />
                创建第一个合同
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Contracts;
